import {
  DashboardCardNameEnum,
  INamedFilterSchemaObject,
  IReportCriteriaFiltersSchemaValues,
  isEmptyFilterValue,
  TDashboardCardFilterKey,
} from "@ryde/shared";

import { DashboardChartType, IDashboardCard } from "./dynamic-dashboard.models";
import { DASHBOARD_SYSTEM_CARDS } from "./dynamic-dashboard.system-cards.constants";
import { IDashboardSystemCardDefinition } from "./dynamic-dashboard.system-cards.models";

/**
 * The filters the card's "open" button should apply: the card's fixed definition,
 * plus whatever the user narrowed THIS card to — otherwise a card showing one
 * supplier would open the page showing every supplier, which is not what the
 * number on the card says.
 *
 * `unmappedKeys` lists any card filter with no control name on the target grid.
 * A PARTIAL user narrowing is never sent: it would under-filter the page, which
 * `useCardFiltersFromQs` refuses to do on arrival for the same reason, so both
 * ends keep to one rule. `fixedFilters` is the card's own definition on its own,
 * which the caller falls back to in that case — dropping those as well would open
 * the whole table, further from the card's number than its un-narrowed population
 * (e.g. every passenger instead of every passenger with no site).
 *
 * Every `supportedFilters` key is mapped today, so this path needs a new filter
 * offered without a mapping to trigger; `system-cards.utils.spec` guards that.
 */
export const buildCardLinkFilters = (
  card: IDashboardCard,
): {
  filters: INamedFilterSchemaObject;
  fixedFilters: INamedFilterSchemaObject;
  unmappedKeys: string[];
} => {
  const definition = getSystemCardDefinition(card.legacyCardName);
  const fixedFilters = { ...(card.linkFilters ?? {}) };
  const filters = { ...fixedFilters };
  const fieldByFilterKey = definition?.filterToLinkField ?? {};
  const unmappedKeys: string[] = [];

  Object.entries(card.cardFilters ?? {}).forEach(([filterKey, value]) => {
    if (isEmptyFilterValue(value)) {
      return;
    }

    const field = fieldByFilterKey[filterKey as TDashboardCardFilterKey];

    if (!field) {
      unmappedKeys.push(filterKey);
      return;
    }

    filters[field] = value;
  });

  return { filters, fixedFilters, unmappedKeys };
};

export const getSystemCardDefinition = (
  cardName: DashboardCardNameEnum | undefined,
): IDashboardSystemCardDefinition | undefined =>
  DASHBOARD_SYSTEM_CARDS.find(item => item.cardName === cardName);

/** Turn a system-card definition into a placeable card. */
export const buildSystemCard = ({
  definition,
  id,
  size = "narrow",
  title,
  cardFilters,
}: {
  definition: IDashboardSystemCardDefinition;
  id: string;
  size?: IDashboardCard["size"];
  title?: string;
  cardFilters?: IDashboardCard["cardFilters"];
}): IDashboardCard => ({
  id,
  title: title?.trim() || definition.title,
  subtitle: "",
  chartType: DashboardChartType.Legacy,
  legacyCardName: definition.cardName,
  size,
  linkTo: definition.linkTo,
  linkFilters: definition.linkFilters,
  // Carried from the definition, not from the previous card: this replaces the
  // card wholesale, so anything not restated here would be lost on first save.
  singleValueFilterKeys: definition.singleValueFilterKeys,
  // Keep only what this card actually supports, so switching the selected card
  // can't leave a filter behind that the backend would silently ignore.
  cardFilters: pickSupportedFilters({ definition, cardFilters }),
});

const pickSupportedFilters = ({
  definition,
  cardFilters,
}: {
  definition: IDashboardSystemCardDefinition;
  cardFilters: IDashboardCard["cardFilters"];
}): IDashboardCard["cardFilters"] => {
  const supported = definition.supportedFilters;

  if (!supported?.length || !cardFilters) {
    return undefined;
  }

  const kept = Object.entries(cardFilters).filter(([key]) =>
    supported.includes(key as TDashboardCardFilterKey),
  );

  return kept.length ? Object.fromEntries(kept) : undefined;
};

export const isLegacyCard = (
  card: IDashboardCard,
): card is IDashboardCard & { legacyCardName: DashboardCardNameEnum } =>
  card.chartType === DashboardChartType.Legacy && Boolean(card.legacyCardName);

export const pickCardSupportedAppliedFilters = ({
  cardName,
  filters,
}: {
  cardName: DashboardCardNameEnum | undefined;
  filters: IReportCriteriaFiltersSchemaValues;
}): IReportCriteriaFiltersSchemaValues => {
  const definition = getSystemCardDefinition(cardName);

  return definition
    ? (pickSupportedFilters({ definition, cardFilters: filters }) ?? {})
    : {};
};
